import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CaretLeft, ChartBar } from '@phosphor-icons/react';
import { useAuth } from '../../../App';
import { SkeletonBlock, EmptyState } from '../../../components/module-ui';
import { getClientEconomics } from '../api';
import { money, StatCard, Panel, MarginCell, EmptyHint, SuperAdminGate, EconomicsSourceNote, LineMoney } from './economicsShared';
import { fmtLabel } from '../ui';

function EconomicsClientDetailPage() {
  const { clientId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [econ, setEcon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('people');

  useEffect(() => {
    if (user?.role !== 'super_admin' || !clientId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getClientEconomics(clientId)
      .then(setEcon)
      .catch((e) => setError(e?.response?.data?.detail || e.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [user?.role, clientId]);

  const summary = econ?.summary || {};
  const currency = econ?.currency || econ?.billing?.currency || 'INR';
  const clientName = econ?.client_name || clientId;
  const teamId = econ?.team_id;
  const productId = econ?.product_id;
  const people = econ?.people || [];
  const infrastructure = econ?.infrastructure || [];
  const devices = econ?.devices || [];

  const tabs = [
    { id: 'people', label: `People (${people.length})` },
    { id: 'infra', label: `Infrastructure (${infrastructure.length})` },
    { id: 'devices', label: `Devices (${devices.length})` },
  ];

  return (
    <div data-testid="economics-client-detail-page">
      <SuperAdminGate user={user}>
        <div style={{ marginBottom: '12px' }}>
          <Link
            to="/dashboard/assets/economics/clients"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#6B7280', textDecoration: 'none' }}
          >
            <CaretLeft size={14} /> All clients
          </Link>
          <h2 style={{ margin: '6px 0 2px', fontSize: '16px', fontWeight: 700 }}>{clientName}</h2>
          <p style={{ margin: 0, fontSize: '12px', color: '#6B7280' }}>
            {econ?.product_name ? `Product: ${econ.product_name}` : 'Client commercial & cost detail'}
            {teamId ? (
              <>
                {' · '}
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/assets/teams/${teamId}/clients/${clientId}`)}
                  style={{ background: 'none', border: 'none', color: '#002FA7', cursor: 'pointer', padding: 0, fontSize: '12px' }}
                >
                  Open in Assets
                </button>
              </>
            ) : null}
          </p>
          <EconomicsSourceNote />
        </div>

        {loading ? <SkeletonBlock rows={8} /> : error ? (
          <EmptyState icon={ChartBar} title="Could not load" description={String(error)} />
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '14px' }}>
              <StatCard
                label="Client pays (monthly)"
                value={money(summary.revenue_monthly, currency)}
                sub={econ?.billing?.amc_amount != null
                  ? `AMC ${money(econ.billing.amc_amount, currency)}/yr ÷ 12`
                  : 'From yearly AMC (recurring)'}
                color="#059669"
              />
              <StatCard
                label="PO (one-time)"
                value={money(summary.revenue_one_time || econ?.billing?.amount, currency)}
                sub={econ?.billing?.po_number || 'Implementation / license'}
                color="#0EA5E9"
              />
              <StatCard label="We spend (monthly)" value={money(summary.spend_monthly, currency)} sub="People + infra + devices" color="#DC2626" />
              <StatCard label="Monthly margin" value={money(summary.margin_monthly, currency)} sub={`${summary.margin_pct ?? 0}% of revenue`} color={(summary.margin_monthly || 0) >= 0 ? '#002FA7' : '#B91C1C'} />
              <StatCard label="People / mo" value={money(summary.spend_people_monthly, currency)} color="#7C3AED" />
              <StatCard label="Infra / mo" value={money(summary.spend_infra_monthly, currency)} color="#D97706" />
              <StatCard label="Devices / mo" value={money(summary.spend_device_monthly, currency)} color="#6366F1" />
              <StatCard label="Purchase allocated" value={money(summary.spend_purchase, currency)} color="#9CA3AF" />
            </div>

            {productId ? (
              <div style={{ marginBottom: '12px' }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  onClick={() => navigate(`/dashboard/assets/economics/products/${encodeURIComponent(productId)}`)}
                >
                  View product economics
                </button>
              </div>
            ) : null}

            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={tab === t.id ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '6px 12px', fontSize: '12px' }}
                  onClick={() => setTab(t.id)}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === 'people' && (
              <Panel title="People cost (Payroll CTC, shared across clients)">
                {people.length === 0 ? <EmptyHint>No members assigned.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Role</th>
                          <th>Clients served</th>
                          <th>Monthly CTC</th>
                          <th>Allocated here</th>
                        </tr>
                      </thead>
                      <tbody>
                        {people.map((p) => (
                          <tr key={p.user_id}>
                            <td style={{ fontWeight: 600 }}>{p.name}</td>
                            <td>{p.org_role || p.designation || '—'}</td>
                            <td>{p.client_count}</td>
                            <td><LineMoney value={p.monthly_cost} currency={currency} /></td>
                            <td><LineMoney value={p.shared_monthly_cost} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {tab === 'infra' && (
              <Panel title="Infrastructure (direct)">
                {infrastructure.length === 0 ? <EmptyHint>No infrastructure costs.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Application / asset</th>
                          <th>Type</th>
                          <th>Environment</th>
                          <th>Hosting</th>
                          <th>Purchase</th>
                          <th>Monthly</th>
                        </tr>
                      </thead>
                      <tbody>
                        {infrastructure.map((row) => (
                          <tr key={row.asset_id}>
                            <td style={{ fontWeight: 600 }}>{row.application_name || row.name}</td>
                            <td style={{ textTransform: 'capitalize' }}>{fmtLabel(row.asset_type)}</td>
                            <td>{row.environment || '—'}</td>
                            <td>{row.hosting_location || '—'}</td>
                            <td><LineMoney value={row.cost} currency={currency} /></td>
                            <td><LineMoney value={row.monthly_cost} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            {tab === 'devices' && (
              <Panel title="Devices (shared from assignees)">
                {devices.length === 0 ? <EmptyHint>No devices allocated.</EmptyHint> : (
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Device</th>
                          <th>Type</th>
                          <th>Assignee</th>
                          <th>Share</th>
                          <th>Purchase alloc.</th>
                          <th>Monthly alloc.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {devices.map((row) => (
                          <tr key={row.asset_id}>
                            <td style={{ fontWeight: 600 }}>{row.name}</td>
                            <td style={{ textTransform: 'capitalize' }}>{fmtLabel(row.asset_type)}</td>
                            <td>{row.assigned_to_name || '—'}</td>
                            <td>{row.share === 'shared' ? `1/${row.client_count}` : 'pinned'}</td>
                            <td><LineMoney value={row.allocated_purchase} currency={currency} /></td>
                            <td><LineMoney value={row.allocated_monthly} currency={currency} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Panel>
            )}

            <Panel title="Margin check">
              <MarginCell value={summary.margin_monthly} pct={summary.margin_pct} alwaysShow />
              <span style={{ fontSize: '12px', color: '#6B7280', marginLeft: '8px' }}>
                revenue {money(summary.revenue_monthly, currency)} − spend {money(summary.spend_monthly, currency)}
              </span>
            </Panel>
          </>
        )}
      </SuperAdminGate>
    </div>
  );
}

export default EconomicsClientDetailPage;
